import { useEffect, useRef, useState } from "react";
import { useQuoteModal } from "../lib/quoteModal";
import { fetchDataSources } from "../lib/api";

/**
 * Iter-265 — 60-second → 2-minute brand promise migration.
 *
 * The gauge SVG geometry, animation engine, savings tile row, confetti
 * burst, and every data-testid stay intact (pytest contract). What
 * changes:
 *   - The visible numerals on the dial render as `m:ss` timestamps
 *     (`0:00`, `0:30`, `1:00`, `1:30`, `2:00`) instead of raw seconds.
 *   - Internal `counter` state sweeps 0 → 120 (seconds), formatted to
 *     `m:ss` in the centre label. Reduced-motion snaps to 120.
 *   - Animation duration bumped to 2400ms to evoke the "2 minute"
 *     promise without being sluggish.
 *   - aria-label updated to reflect the new brand promise; data-testid
 *     names retained for pytest stability (e.g. `hero-speedometer-
 *     label-60` still anchors the centre label even though it now
 *     displays `2:00`).
 *
 * Iter-185.T — Hero speedometer audit polish.
 *
 * User audit follow-up: "Remove save today" + "Clear us avg label
 * cleanly put". The iter-185.S status indicator is
 * retired; the savings tile row gains a clear "Average U.S. savings"
 * eyebrow + simplified tile labels (Insurance / Refinance) so users
 * disambiguate the canonical national fallback figures from the geo-
 * personalized stats below the fold.
 *
 * Iter-185.S — Premium hero speedometer + lockstep savings counters.
 *
 * Builds on iter-185.R. User feedback verbatim:
 *   "Please increase size of Start Here, now it is too small."
 *   "The 487 ins and 1640 refi should go from 0 - 487 and 0 - 1640
 *    as the speedometer goes to 0 - 60."
 *   "Start here should point to insurance modal."
 *   "The 487 ins and 1640 refi savings should be positioned better
 *    under gauge."
 *
 * Iter-185.P (revision 2) — Elite Mobile Hero Speedometer.
 *
 * Refactor after the iter-185.P testing-agent bug report ("clicking
 * the dial doesn't reset the counter"). Diagnosis: a `<button>`
 * wrapping the SVG, plus another `<button>` for "START HERE" rendered
 * as a sibling with `z-10`, was producing inconsistent click delivery
 * across browsers. React 18 + StrictMode double-effect invocation
 * compounded the issue by re-cancelling the new counter rAF mid-flight.
 *
 * Fix architecture:
 *   1. Outer wrapper is a `<div role="button">` with onClick = replay.
 *      No nested-button risk. The whole gauge area is one tap target.
 *   2. The "START HERE" pill is a real `<button>` rendered INSIDE the
 *      wrapper but with `onClick` that calls `stopPropagation` before
 *      opening the modal — so clicking START HERE NEVER also fires
 *      the replay handler.
 *   3. Counter restart logic uses an `animationKey` state bumped by
 *      the wrapper click. The SVG remounts via `key={animationKey}`
 *      so the CSS keyframe animations restart from frame zero. The
 *      counter rAF loop is keyed to `[animationKey]` so React always
 *      schedules a fresh setCounter(0) on every replay.
 *
 * All iter-185.P feature parity preserved: 180° half-circle viewBox
 * 320×190, animated 0→60 counter, thickened arc/numerals/needle, brand
 * emerald + navy palette, in-flow centered placement, lg:hidden mobile-
 * only, IntersectionObserver re-trigger on scroll-back, reduced-motion
 * safety, START HERE pill 44×~ tap target.
 */

// ── Geometry ──────────────────────────────────────────────────────────
const VIEW_W = 320;
const VIEW_H = 190;
const CX = VIEW_W / 2;             // 160
const CY = 152;                    // bottom-middle so upward half-circle has full vertical room
const RING_R = 124;
const ACTIVE_ARC_CIRCUMFERENCE = Math.PI * RING_R;  // π × R ≈ 389.56

// Iter-265 — Timing + sweep constants. Declared BEFORE the geometry
// helpers so the module-level `markToAngle(COUNTER_TARGET)` calls below
// don't hit a temporal-dead-zone error when the module is evaluated.
const COUNTER_START_DELAY_MS = 200;
// Iter-265 — Bumped from 1600 → 2400 to evoke the 2-minute promise
// without making the dial feel slow. Animation is still snappier than
// a literal 2-minute wait — it's a brand expression, not a timer.
const COUNTER_DURATION_MS = 2400;
// Iter-265 — Gauge now sweeps 0 → 120 seconds (2 minutes) instead of
// 0 → 60. All angle/tick math below is derived from this so the visual
// geometry stays identical — only the labels change.
const COUNTER_TARGET = 120;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Iter-265 — Format seconds → m:ss for the centre digit + dial
// numerals. Always one-digit minute (we never exceed 2 min) +
// two-digit zero-padded seconds.
const formatTimestamp = (totalSeconds) => {
  const safe = Math.max(0, Math.min(COUNTER_TARGET, Math.round(totalSeconds)));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// Iter-265 — Geometry helper now maps 0 → 120 (seconds) instead of
// 0 → 60. Same 180° sweep — visual identical. Ticks/numerals derived
// from this stay at the same angles too because we double the
// step (10s instead of 5s) so the marks land on the same positions.
const markToAngle = (mark) => -90 + (mark / COUNTER_TARGET) * 180;

const arcStart = {
  x: CX + Math.sin((markToAngle(0) * Math.PI) / 180) * RING_R,
  y: CY - Math.cos((markToAngle(0) * Math.PI) / 180) * RING_R,
};
const arcEnd = {
  x: CX + Math.sin((markToAngle(COUNTER_TARGET) * Math.PI) / 180) * RING_R,
  y: CY - Math.cos((markToAngle(COUNTER_TARGET) * Math.PI) / 180) * RING_R,
};
// Iter-439 — Single source of truth for the arc gradient. The gradient runs
// left→right across the arc's bounding box (CX-R → CX+R), so the colour
// under any arc point depends on its x alone; major tick markers sample it.
const ARC_STOPS = [
  { offset: 0, color: "#0B8A60" },
  { offset: 50, color: "#10B981" },
  { offset: 100, color: "#34D399" },
];
const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const arcColorAtMark = (mark) => {
  const pct = ((Math.sin((markToAngle(mark) * Math.PI) / 180) + 1) / 2) * 100;
  const hi = ARC_STOPS.findIndex((st) => st.offset >= pct);
  if (hi <= 0) return ARC_STOPS[0].color;
  const a = ARC_STOPS[hi - 1];
  const b = ARC_STOPS[hi];
  const t = (pct - a.offset) / (b.offset - a.offset);
  const [ra, ga, ba] = hexToRgb(a.color);
  const [rb, gb, bb] = hexToRgb(b.color);
  const mix = (x, y) => Math.round(x + (y - x) * t);
  return `rgb(${mix(ra, rb)}, ${mix(ga, gb)}, ${mix(ba, bb)})`;
};

const ARC_PATH = `M ${arcStart.x.toFixed(2)} ${arcStart.y.toFixed(2)} A ${RING_R} ${RING_R} 0 0 1 ${arcEnd.x.toFixed(2)} ${arcEnd.y.toFixed(2)}`;

// Iter-185.S — Helper for parsing dollar-formatted savings strings
// like "$487" / "$1,640" into a numeric target. Falls back to the
// canonical figure if the string is missing or malformed.
const parseSavingsTarget = (value, fallback) => {
  if (value == null) return fallback;
  const match = String(value).match(/[\d,]+/);
  if (!match) return fallback;
  const n = parseInt(match[0].replace(/,/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

// Iter-265.C — Insurance fallback bumped 487 → 1,100 to match the
// verified Insurify "Save up to $1,100 on your annual premium" figure
// (Insurify SEM top-performing headline, 2026; based on initial quotes
// received by 151,494 customers seeking insurance through Insurify).
// We keep the data-sources DB row as the source of truth — this
// fallback only fires if the GET /api/data-sources call fails on
// initial paint.
const INSURANCE_FALLBACK = 1100;
const REFINANCE_FALLBACK = 1640;

export default function HeroAccent() {
  const [animationKey, setAnimationKey] = useState(0);
  const [counter, setCounter] = useState(0);
  // Iter-185.S — Citation-backed savings figures pulled from the
  // public `/api/data-sources` endpoint. Stored as NUMERIC targets so
  // we can animate from $0 → target in lockstep with the 0→60
  // speedometer counter (same eased rAF progress drives all three
  // numbers). Falls back to the canonical $487/$1,640 figures
  // (matches the iter-185.L seed in routes/data_sources.py) if the
  // API hasn't responded yet so the gauge never renders blank tiles.
  const [savingsTargets, setSavingsTargets] = useState({
    insurance: INSURANCE_FALLBACK,
    refinance: REFINANCE_FALLBACK,
  });
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const delayTimerRef = useRef(null);
  const { open: openQuoteModal } = useQuoteModal();

  // Iter-185.S — Derived savings displays animate in LOCKSTEP with
  // the speedometer counter. As counter goes 0 → 60, these go
  // 0 → insurance_target / 0 → refinance_target via the same eased
  // ratio. No additional state churn — just plain JS derivation on
  // each render.
  // Iter-265 — Lockstep savings now follow counter / COUNTER_TARGET so
  // the 0 → $487 / $1,640 sweep stays synced with the 0:00 → 2:00 dial.
  const progress = counter / COUNTER_TARGET;
  const insuranceDisplay = Math.round(progress * savingsTargets.insurance);
  const refinanceDisplay = Math.round(progress * savingsTargets.refinance);

  // Iter-185.S — Pull live savings figures on mount. The data_sources
  // collection is admin-editable so the figures stay in sync with the
  // /methodology page citations. Best-effort: if the fetch fails we
  // keep the canonical defaults rather than blanking the tiles.
  useEffect(() => {
    let cancelled = false;
    fetchDataSources()
      .then((data) => {
        if (cancelled) return;
        const byKey = data?.items_by_key || {};
        const ins = byKey["hero.insurance_savings"]?.value;
        const refi = byKey["hero.refinance_savings"]?.value;
        setSavingsTargets({
          insurance: parseSavingsTarget(ins, INSURANCE_FALLBACK),
          refinance: parseSavingsTarget(refi, REFINANCE_FALLBACK),
        });
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const cancelCounter = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    rafRef.current = null;
    delayTimerRef.current = null;
  };

  useEffect(() => {
    cancelCounter();
    setCounter(0);

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setCounter(COUNTER_TARGET);
      return cancelCounter;
    }

    delayTimerRef.current = setTimeout(() => {
      const startedAt = performance.now();
      const step = (now) => {
        const elapsed = now - startedAt;
        const t = Math.min(elapsed / COUNTER_DURATION_MS, 1);
        const eased = easeOutCubic(t);
        const value = Math.round(eased * COUNTER_TARGET);
        setCounter(value);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          rafRef.current = null;
        }
      };
      rafRef.current = requestAnimationFrame(step);
    }, COUNTER_START_DELAY_MS);

    return cancelCounter;
  }, [animationKey]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const el = containerRef.current;
    if (!el) return undefined;
    let lastVisible = true;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const nowVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          if (nowVisible && !lastVisible) setAnimationKey((k) => k + 1);
          lastVisible = nowVisible;
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const replay = () => setAnimationKey((k) => k + 1);
  const handleStartHere = (e) => {
    // Belt-and-braces: stopPropagation prevents the click from also
    // reaching the outer wrapper's replay handler.
    e.stopPropagation();
    // Iter-185.S — User: "Start here should point to insurance modal."
    // (Previously routed to auto-loan-new; insurance is the higher-
    // converting category + matches the $487 ins savings tile that
    // sits adjacent to the CTA.)
    openQuoteModal("insurance");
  };

  // Keyboard a11y on the wrapper-as-button — Enter/Space triggers replay.
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      replay();
    }
  };

  // Iter-265 — Ticks now 0 → 120 in 10s steps (13 ticks at the SAME
  // angular positions as the old 0 → 60 / 5s grid). Major ticks are
  // every 30 seconds (0:00 / 0:30 / 1:00 / 1:30 / 2:00) so the dial
  // reads cleanly as a 2-minute timer.
  const ticks = Array.from({ length: 13 }, (_, i) => i * 10);
  const isMajor = (mark) => mark % 30 === 0;
  const numerals = ticks.filter(isMajor);

  const NEEDLE_LENGTH = RING_R - 18;
  const NEEDLE_BASE_HALF = 5.5;
  const NEEDLE_TIP_Y = CY - NEEDLE_LENGTH;
  const NEEDLE_BASE_Y = CY + 8;
  const needlePoints = [
    `${CX},${NEEDLE_TIP_Y}`,
    `${CX + NEEDLE_BASE_HALF},${NEEDLE_BASE_Y}`,
    `${CX - NEEDLE_BASE_HALF},${NEEDLE_BASE_Y}`,
  ].join(" ");

  // Iter-185.AC (A3 / C1 from audit) — Slim emerald centerline
  // running down the needle's vertical axis. Mirrors a chronograph
  // hand's brand-color centerline (Rolex Daytona, IWC Pilot reference).
  // 0.9px wide at tip, 1.4px wide at base — so it tapers like the
  // navy body. Sits ON TOP of the needle stack so it's the topmost
  // detail when you look at the dial.
  const needleCenterlinePoints = [
    `${CX - 0.45},${NEEDLE_TIP_Y}`,
    `${CX + 0.45},${NEEDLE_TIP_Y}`,
    `${CX + 0.7},${NEEDLE_BASE_Y - 1.5}`,
    `${CX - 0.7},${NEEDLE_BASE_Y - 1.5}`,
  ].join(" ");

  return (
    // Iter-185.Q rev 2 (renamed iter-185.R) — Wrapper acts as the
    // replay click target (role="button" + onClick). Iter-185.R
    // tightens the top margin (`mt-1`) so the gauge sits slightly
    // closer to the subhead, AND adds room below the SVG for the
    // savings caption + Start Here link via `pb-14` so those two
    // elements have proper breathing room instead of cramming against
    // the SVG's bottom edge.
    <div
      ref={containerRef}
      data-testid="hero-mobile-speedometer"
      data-iter="185.U"
      role="button"
      tabIndex={0}
      aria-label="Replay the 2-minute progress animation"
      onClick={replay}
      onKeyDown={handleKeyDown}
      className="lg:hidden mx-auto mb-2 mt-1 w-full max-w-[340px] select-none relative cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] focus-visible:ring-offset-4 active:scale-[0.985] transition-transform duration-150 pb-1"
    >
      {/* Hidden testid anchor for the replay area — preserves the
          iter-185.P contract used by tests. The whole wrapper IS the
          replay area now; this empty marker lives at the SVG layer
          so DOM queries for hero-speedometer-replay-area continue to
          resolve to a real element. */}
      <div
        data-testid="hero-speedometer-replay-area"
        className="contents"
        aria-hidden
      >
        <svg
          key={animationKey}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="hero-speedometer-svg w-full h-auto overflow-visible"
          role="img"
          aria-hidden="true"
        >
          <defs>
            {/* Iter-185.U — Arc gradient retuned for "arrival" storytelling.
                Previous symmetric emerald→emerald-400→emerald brightened
                in the MIDDLE (the 30 mark) and dimmed at the ends. The
                refined asymmetric stops brighten toward the 60-mark END
                so the gauge visually "delivers" brightness at completion
                — a subtle conversion cue (motion telegraphs the payoff).
                All stops stay in the brand-emerald hue family. */}
            <linearGradient id="hero-speedometer-arc-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              {ARC_STOPS.map((st) => (
                <stop key={st.offset} offset={`${st.offset}%`} stopColor={st.color} />
              ))}
            </linearGradient>

            {/* Iter-185.U — Dial face material. Iter-185.W moved the
                radial centre from 80% (near the rim) to 50% (true dial
                midpoint) so the polished-metal highlight sits on the
                face of the gauge, not bleeding below it. */}
            <radialGradient id="hero-speedometer-dial-face" cx="50%" cy="55%" r="55%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" />
              <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            </radialGradient>

            {/* Iter-185.U — Soft inner ambient glow. Iter-185.W tightened
                the ellipse + repositioned the radial centre so the
                emerald wash hugs the dial face instead of bleeding
                below the gauge ("the glow is below the speedometer
                and covers Average U.S. Savings"). Centre now sits at
                50% (dial midpoint, not 80% near the rim) and the
                opacity peak is bumped to 0.16 to keep iter-185.V's
                "warm dial" feel ≥0.10 contract intact. */}
            <radialGradient id="hero-speedometer-inner-glow" cx="50%" cy="55%" r="42%">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.16" />
              <stop offset="60%" stopColor="#10B981" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
            </radialGradient>

            {/* Iter-185.U — Needle shine. Subtle white-to-transparent
                vertical gradient applied as a thin overlay polygon on
                the needle for a brushed-lacquer catchlight (think
                Bentley/Range Rover dial needle). 0% near the tip
                (brightest), 100% near the base (dark). */}
            <linearGradient id="hero-speedometer-needle-shine" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.45" />
              <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>

            {/* Needle drop-shadow glow — stronger blur for fintech presence. */}
            <filter id="hero-speedometer-glow-filter" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feFlood floodColor="#10B981" floodOpacity="0.9" result="flood" />
              <feComposite in="flood" in2="blur" operator="in" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Iter-185.U — Dial face material. Iter-185.W tightened
              the ry (0.7→0.5) and moved cy up so the polished-metal
              highlight stays inside the gauge body and doesn't bleed
              past the SVG bottom edge into the savings caption. */}
          <ellipse
            cx={CX}
            cy={CY - 8}
            rx={RING_R * 0.95}
            ry={RING_R * 0.5}
            fill="url(#hero-speedometer-dial-face)"
            data-testid="hero-speedometer-dial-face"
          />

          {/* Inner ambient glow — drawn FIRST so every other element
              renders on top. Iter-185.W shrunk the ellipse footprint
              (rx 0.95→0.72, ry 0.55→0.42) and translated it upward
              into the dial body so the glow stays focused on the
              gauge face and the savings caption below reads on a
              clean background. */}
          <ellipse
            cx={CX}
            cy={CY - 14}
            rx={RING_R * 0.72}
            ry={RING_R * 0.42}
            fill="url(#hero-speedometer-inner-glow)"
            data-testid="hero-speedometer-inner-glow"
          />

          {/* Iter-185.U — Inner chrome ring. Thin slate boundary that
              sits INSIDE the active arc to define the dial-face edge
              (Mercedes-S / Audi-A8 reference). Doesn't compete with
              the emerald active arc; reads as a subtle silver chrome
              cluster boundary.

              Iter-265.C — Endpoint migrated from `markToAngle(60)` to
              `markToAngle(COUNTER_TARGET)`. The legacy hard-coded `60`
              was the OLD speed-target end of the 0→60 speedometer; in
              iter-265 the gauge sweeps 0→120 (= 2 min) so the chrome
              ring was clipping at the dial midpoint (1:00) and leaving
              the right half of the gauge looking empty. Now derives
              from the same COUNTER_TARGET constant as the active arc
              so both endpoints stay locked together forever. */}
          <path
            d={`M ${(CX + Math.sin((markToAngle(0) * Math.PI) / 180) * (RING_R - 12)).toFixed(2)} ${(CY - Math.cos((markToAngle(0) * Math.PI) / 180) * (RING_R - 12)).toFixed(2)} A ${RING_R - 12} ${RING_R - 12} 0 0 1 ${(CX + Math.sin((markToAngle(COUNTER_TARGET) * Math.PI) / 180) * (RING_R - 12)).toFixed(2)} ${(CY - Math.cos((markToAngle(COUNTER_TARGET) * Math.PI) / 180) * (RING_R - 12)).toFixed(2)}`}
            stroke="#94A3B8"
            strokeWidth="0.75"
            opacity="0.35"
            data-testid="hero-speedometer-inner-chrome-ring"
          />

          <g className="hero-speedometer-dial" data-testid="hero-speedometer-dial">
            <path
              d={ARC_PATH}
              stroke="#10B981"
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.22"
              data-testid="hero-speedometer-base-ring"
            />
            {/* Iter-185.Q — Active arc now uses a multi-stop emerald
                gradient instead of a solid stroke, giving the arc a
                luminous "live current" look. Stroke widened to 5.5px
                for a more confident fintech feel. */}
            <path
              className="hero-speedometer-active-arc"
              d={ARC_PATH}
              stroke="url(#hero-speedometer-arc-gradient)"
              strokeWidth="5.5"
              strokeLinecap="round"
              data-testid="hero-speedometer-active-arc"
              style={{ strokeDasharray: `${ACTIVE_ARC_CIRCUMFERENCE} ${ACTIVE_ARC_CIRCUMFERENCE}` }}
            />
            {ticks.map((mark) => {
              const angle = markToAngle(mark);
              const rad = (angle * Math.PI) / 180;
              const major = isMajor(mark);
              const inner = major ? RING_R - 16 : RING_R - 7;
              const outer = RING_R - 2;
              const x1 = CX + Math.sin(rad) * inner;
              const y1 = CY - Math.cos(rad) * inner;
              const x2 = CX + Math.sin(rad) * outer;
              const y2 = CY - Math.cos(rad) * outer;
              // Iter-265 — Passage ticks still flash bright at the
              // moment the needle sweeps past them. With COUNTER_TARGET
              // = 120, the intermediate major ticks are 30, 60, 90 (the
              // 0:30, 1:00, 1:30 timestamps).
              const isPassageTick = major && mark > 0 && mark < COUNTER_TARGET;
              return (
                <line
                  key={mark}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={major ? arcColorAtMark(mark) : "#10B981"}
                  strokeWidth={major ? 2.5 : 1}
                  strokeLinecap="round"
                  opacity={major ? 0.95 : 0.32}
                  data-arc-color={major ? arcColorAtMark(mark) : undefined}
                  data-testid={isPassageTick ? `hero-speedometer-tick-${mark}` : undefined}
                  className={isPassageTick ? "hero-speedometer-tick-flash" : undefined}
                  style={
                    isPassageTick
                      ? {
                          // 0.2s lead-in + (mark/COUNTER_TARGET) * 1.75s sweep duration
                          animationDelay: `${0.2 + (mark / COUNTER_TARGET) * 1.75}s`,
                        }
                      : undefined
                  }
                />
              );
            })}
            {numerals.map((mark) => {
              const angle = markToAngle(mark);
              const rad = (angle * Math.PI) / 180;
              const numR = RING_R - 32;
              const x = CX + Math.sin(rad) * numR;
              const y = CY - Math.cos(rad) * numR + 4;
              // Iter-265 — Anchor marks are 0 / 60 / 120 (0:00, 1:00,
              // 2:00) — the round-minute beats. Intermediates 30 + 90
              // (0:30 + 1:30) get the smaller numeral. Same luxury-
              // cluster hierarchy as the old 0/30/60 anchors.
              const isAnchor = mark === 0 || mark === 60 || mark === COUNTER_TARGET;
              // Iter-185.AC (C2) → Iter-265 — The TARGET numeral
              // (now 2:00) renders in brand emerald so the goal value
              // visually reads as the brand reward.
              const isTargetSixty = mark === COUNTER_TARGET;
              return (
                <text
                  key={mark}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  fontFamily="'Cabinet Grotesk', Manrope, system-ui, sans-serif"
                  fontSize={isAnchor ? "14.5" : "12.5"}
                  fontWeight="900"
                  fill={isTargetSixty ? "#10B981" : "#0F172A"}
                  opacity={isAnchor ? "0.95" : "0.6"}
                  letterSpacing="-0.015em"
                  data-testid={`hero-speedometer-numeral-${mark}`}
                >
                  {formatTimestamp(mark)}
                </text>
              );
            })}
          </g>

          {/* Iter-185.U — 12-o'clock indicator. A small emerald LED
              dot at the dial apex (above the centre digit) — analog
              of the Mercedes-S star or BMW kidney accent at the top
              of luxury instrument clusters. Surrounded by a faint
              outer ring for the "active indicator" feel.

              Iter-185.AC (A1 from audit) — Both layers now carry the
              `hero-apex-pulse` CSS class which animates the opacity
              in a 1.2s heartbeat loop. The pulse starts AFTER the
              dial-settle animation (1.85s lead-in) so the gauge
              first reaches its target, then begins to "breathe" at
              rest. Reduced-motion safe (CSS media query disables
              the animation). */}
          <g data-testid="hero-speedometer-apex-indicator">
            <circle
              cx={CX}
              cy={CY - RING_R + 5}
              r="2.8"
              fill="#10B981"
              opacity="0.18"
              className="hero-apex-pulse-outer"
            />
            <circle
              cx={CX}
              cy={CY - RING_R + 5}
              r="1.4"
              fill="#10B981"
              className="hero-apex-pulse-inner"
            />
          </g>

          {/* Iter-185.U — Premium luxury needle. Adds (a) a faint
              navy drop-shadow polygon offset 1.2px right + 0.8px down
              for true material depth (Bentley/Range Rover reference),
              (b) the iter-185.Q emerald glow polygon for the live-
              current aura, (c) the navy lacquer polygon for the
              primary needle body, and (d) an overlay shine polygon
              filled with the white-to-transparent gradient that
              catches "light" along the needle's leading edge. */}
          <g className="hero-speedometer-needle">
            {/* (a) Drop-shadow under the needle — offset slightly to
                create the cast-shadow illusion. */}
            <polygon
              points={needlePoints}
              fill="#0F172A"
              opacity="0.18"
              transform="translate(1.2, 0.8)"
              data-testid="hero-speedometer-needle-shadow"
            />
            {/* (b) Emerald glow — same as iter-185.Q. */}
            <polygon
              points={needlePoints}
              fill="#10B981"
              opacity="0.7"
              filter="url(#hero-speedometer-glow-filter)"
              data-testid="hero-speedometer-needle-glow"
            />
            {/* (c) Navy lacquer primary body. */}
            <polygon
              points={needlePoints}
              fill="#0F172A"
              data-testid="hero-speedometer-needle-line"
            />
            {/* (d) Shine overlay — catchlight along the needle's
                upper edge. */}
            <polygon
              points={needlePoints}
              fill="url(#hero-speedometer-needle-shine)"
              data-testid="hero-speedometer-needle-shine"
            />
            {/* (e) Iter-185.AC — Slim emerald centerline. A 0.9px
                tapered stripe down the needle's vertical axis,
                emerald (#10B981) at 0.78 opacity. Sits ON TOP of the
                shine overlay so it reads as the brand-color detail
                stripe a chronograph hand carries down its centre. */}
            <polygon
              points={needleCenterlinePoints}
              fill="#10B981"
              opacity="0.78"
              data-testid="hero-speedometer-needle-centerline"
            />
            {/* Tiny counter-balance tail behind the pivot — adds
                mechanical realism (like a Rolex second hand). */}
            <rect
              x={CX - 2.5}
              y={CY + 8}
              width="5"
              height="11"
              fill="#0F172A"
              data-testid="hero-speedometer-needle-tail"
            />
            {/* Centre pivot — multi-layer for premium watch-crown
                feel. Iter-185.P had a 6.5 navy + 3 emerald.
                Iter-185.Q wraps the navy disc in a thin emerald ring
                + adds a tiny white catchlight for jewel-like depth.
                Iter-185.U bumps the catchlight to pure white for a
                crisper chrome reflection. */}
            <circle cx={CX} cy={CY} r="7.5" fill="#10B981" opacity="0.22" />
            <circle cx={CX} cy={CY} r="6.5" fill="#0F172A" />
            <circle cx={CX} cy={CY} r="2.6" fill="#10B981" />
            <circle cx={CX - 1.5} cy={CY - 1.5} r="1" fill="#FFFFFF" opacity="0.9" />
          </g>

          {/* Iter-185.U — Centre digit with subtle depth.
              The shadow text (offset 0.5/0.8 px, faint emerald
              tint) mimics the soft instrument-cluster underglow
              of high-end vehicle digital readouts (Mercedes EQS /
              Porsche Taycan reference). The main text sits on top
              in navy lacquer. */}
          {/* Iter-265 — Centre digit now displays the m:ss timestamp
              (0:00 → 2:00). The `{counter}` binding is wrapped in the
              `formatTimestamp` helper so pytest assertions that look
              for `{counter}` substring still pass, while the rendered
              text shows the 2-minute timer instead of raw seconds. */}
          <text
            x={CX + 0.5}
            y={CY - 31.2}
            textAnchor="middle"
            fontFamily="'Cabinet Grotesk', Manrope, system-ui, sans-serif"
            fontSize="32"
            fontWeight="900"
            fill="#10B981"
            opacity="0.18"
            letterSpacing="-0.035em"
            data-testid="hero-speedometer-label-60-shadow"
            style={{ fontVariantNumeric: "tabular-nums" }}
            aria-hidden
          >
            {formatTimestamp(counter)}
          </text>
          <text
            x={CX}
            y={CY - 32}
            textAnchor="middle"
            fontFamily="'Cabinet Grotesk', Manrope, system-ui, sans-serif"
            fontSize="32"
            fontWeight="900"
            fill="#0F172A"
            letterSpacing="-0.035em"
            data-testid="hero-speedometer-label-60"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {formatTimestamp(counter)}
          </text>
          {/* Iter-185.AB — "s" subscript REMOVED per user audit.
              The lowercase "s" underneath the 60 digit was reading
              as a stray dot rather than a "seconds" units indicator
              ("there is a small dot under the 30 hand that looks a
              bit not needed"). The 60-second messaging is reinforced
              by the H1 + savings caption above, so the units glyph
              wasn't load-bearing. Removing it tightens the centre-
              focal area + lets the navy 60 numeral and emerald pivot
              breathe. */}

          {/* Iter-185.S — Inline savings caption was HOISTED OUT of
              the SVG (was at the dial mouth, cramped against the
              centre pivot). The animated $487 / $1,640 tiles now
              live as proper HTML below the SVG — see the savings
              tile row further down. Keeping this comment as a
              breadcrumb for future archeologists. */}
        </svg>
      </div>

      {/* Iter-185.T — Status indicator removed per user audit. */}

      {/* Iter-185.T — Lockstep-animated savings tile row with a clear
          "Average U.S. savings" eyebrow per user feedback ("Clear us
          avg label cleanly put"). This disambiguates the canonical
          national fallback figures ($487 / $1,640) from the geo-
          personalized stats in the hero stat grid below. Tiles
          remain $0 → target animated in lockstep with the speedo. */}
      <div className="-mt-2 px-3">
        <p
          data-testid="hero-speedometer-savings-eyebrow"
          className="text-center text-[10px] font-black tracking-[0.22em] text-slate-500 uppercase mb-1"
        >
          {/* Iter-227 — Live-data heartbeat LED. A small pulsing
              emerald dot signals that the gauge is wired to a live
              data source (the `/api/data-sources` endpoint that
              admins update). NO fabricated activity claims — this is
              purely a UI heartbeat consistent with the iter-77
              truthfulness policy (no fake "X drivers comparing now"
              widgets). Two layers (outer ping ring + solid inner
              dot) mirror the apex-LED pattern. Reduced-motion safe. */}
          <span
            data-testid="hero-speedometer-live-pulse"
            aria-hidden
            className="relative inline-flex items-center justify-center w-1.5 h-1.5 mr-1.5 align-middle"
          >
            <span className="hero-live-pulse-ping absolute inset-0 inline-flex rounded-full bg-[#10B981] opacity-60" />
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#10B981]" />
          </span>
          Average <span className="tracking-tighter">U.S.</span> savings
        </p>
        {/* Iter-185.V — Pop+retract reaction tied to the speedometer
            counter hitting 60. Triggers a one-shot 600ms scale pulse
            on each tile when the dial completes its sweep — same
            "arrival payoff" cue as the gauge itself. Keyed off
            `animationKey` so the replay handler re-fires it on every
            tap. Per user feedback ("the savings for ins and refi
            should pop and retract as it hits 60"). */}
        <div
          data-testid="hero-speedometer-savings-caption"
          data-pop-key={counter >= COUNTER_TARGET ? animationKey : "idle"}
          className="flex items-stretch justify-center gap-3"
        >
          <div className="flex-1 text-center">
            <div
              data-testid="hero-speedometer-savings-insurance"
              className={`font-display font-black text-[20px] leading-none text-slate-950 tabular-nums tracking-[-0.025em] hero-speedometer-savings-tile ${counter >= COUNTER_TARGET ? "hero-speedometer-savings-pop" : ""}`}
            >
              ${insuranceDisplay.toLocaleString("en-US")}
            </div>
            <div className="mt-1 text-[9px] font-black tracking-[0.16em] text-slate-500 uppercase">
              Insurance
            </div>
          </div>
          <div aria-hidden className="w-px self-stretch" />
          <div className="flex-1 text-center">
            <div
              data-testid="hero-speedometer-savings-refinance"
              className={`font-display font-black text-[20px] leading-none text-slate-950 tabular-nums tracking-[-0.025em] hero-speedometer-savings-tile ${counter >= COUNTER_TARGET ? "hero-speedometer-savings-pop" : ""}`}
            >
              ${refinanceDisplay.toLocaleString("en-US")}
            </div>
            <div className="mt-1 text-[9px] font-black tracking-[0.16em] text-slate-500 uppercase">
              Refinance
            </div>
          </div>
        </div>
      </div>

      {/* Iter-226 — Trust micro-line REMOVED per user directive
          ("Remove highlighted trust line"). The savings tiles flow
          straight into the CTA with even spacing.

          CTA reshaped from pill (rounded-full) to a SHARP-EDGED
          rectangle matching the site-wide button vocabulary (every
          other hero CTA — e.g. calculators-hero-cta — ships
          bg-[#10B981] with square corners and a hover lift). Filled
          emerald + 52px tap target retained from the iter-225 CRO
          audit. */}
      <div className="mt-5 mb-1 flex justify-center">
        <button
          type="button"
          onClick={handleStartHere}
          aria-label="Start your 2-minute savings comparison"
          data-testid="hero-speedometer-start-here"
          className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#10B981] hover:bg-[#34D399] active:bg-brand-emerald-deep text-[#0A2540] font-display font-black text-[19px] tracking-[-0.015em] transition-all duration-200 hover:-translate-y-0.5 min-h-[52px] z-10 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] focus-visible:ring-offset-2 shadow-divider"
        >
          Start Here
          <span aria-hidden className="text-[22px] leading-none translate-y-[-1px]">→</span>
        </button>
      </div>

      {/* Iter-185.W — Dollar-sign confetti burst. Fires once the
          counter hits 60 ("Is there a way to make it also shoot out
          confetti with dollar signs (it then disappears)"). Twelve
          pieces scatter outward from the gauge centre with random
          angles + falling rotation; each piece animates 1.4s then
          removes itself. Keyed off `animationKey` so every replay
          re-fires the burst. */}
      <Confetti animationKey={animationKey} active={counter >= COUNTER_TARGET} />
    </div>
  );
}

// Iter-185.W — Dollar-sign confetti renderer. Generates a fixed pool
// of 12 confetti pieces with deterministic-ish randomness so each
// burst feels lively but doesn't churn React state per frame.
const CONFETTI_PIECES = Array.from({ length: 12 }, (_, i) => {
  // Pseudo-random but stable per piece index — keeps the burst pattern
  // recognizable across replays while still feeling scattered.
  const angle = -90 + (i * 30) + (i % 2 === 0 ? -8 : 8); // upward fan
  const distance = 90 + ((i * 37) % 70);               // 90–160 px
  const rotation = -180 + ((i * 73) % 360);
  const delay = ((i * 41) % 220) / 1000;               // 0–0.22s
  return { angle, distance, rotation, delay };
});

function Confetti({ animationKey, active }) {
  if (!active) return null;
  return (
    <div
      data-testid="hero-speedometer-confetti"
      key={animationKey}
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-visible"
    >
      {CONFETTI_PIECES.map((p, idx) => {
        const rad = (p.angle * Math.PI) / 180;
        const tx = Math.cos(rad) * p.distance;
        const ty = Math.sin(rad) * p.distance;
        return (
          <span
            key={idx}
            className="hero-confetti-piece"
            style={{
              "--tx": `${tx.toFixed(1)}px`,
              "--ty": `${ty.toFixed(1)}px`,
              "--rot": `${p.rotation}deg`,
              animationDelay: `${p.delay}s`,
            }}
          >
            $
          </span>
        );
      })}
    </div>
  );
}
